import React from "react";
import { motion } from "framer-motion";
import { Reveal, stagger, fadeUp } from "./Reveal";
import { Check, ArrowUpRight, User, Users, Heart, Leaf, TrendingUp, Target, Sprout } from "lucide-react";
import { CONSULTATIONS, PACKAGES, FLASH } from "../data/site";

const PriceBox = ({ label, total, per, tone }) => (
  <div className={`flex-1 rounded-2xl px-4 py-3 text-center ${tone === "offline" ? "bg-[#F7EBC6]/70 border border-[#EADB9B]" : "bg-white border border-[#C9DFAC]"}`}>
    <div className="text-[0.6rem] font-mono uppercase tracking-widest text-[#55703F]">{label}</div>
    <div className="font-serif text-2xl text-[#20301A] mt-0.5">{total}</div>
    {per && <div className="text-[0.68rem] text-[#55703F]">({per} per session)</div>}
  </div>
);

const PackageCard = ({ p, icon: Icon, accent, onBuy }) => (
  <motion.div variants={fadeUp} data-testid={`package-card-${p.key}`}
    className="rounded-3xl border border-[#EBD4DC] bg-white/70 soft-shadow p-6 sm:p-7 flex flex-col">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-11 h-11 rounded-full shrink-0" style={{ backgroundColor: accent.soft, color: accent.ink }}><Icon size={20} /></span>
        <div>
          <h4 className="font-serif text-2xl leading-tight text-[#20301A]">{p.name}</h4>
          <p className="text-xs text-[#55703F] mt-0.5">{p.sessions}</p>
        </div>
      </div>
      <span className="text-[0.62rem] font-mono uppercase tracking-widest text-white px-2.5 py-1 rounded-full shrink-0" style={{ backgroundColor: accent.ink }}>{p.discount}</span>
    </div>

    <ul className="mt-5 space-y-2 text-sm text-[#55703F] flex-1">
      {p.points.map((li) => (
        <li key={li} className="flex items-start gap-2"><Check size={15} className="shrink-0 mt-0.5" style={{ color: accent.ink }} /> {li}</li>
      ))}
    </ul>

    <div className="mt-5 flex gap-3">
      <PriceBox label="Online" total={p.online.total} per={p.online.per} tone="online" />
      <PriceBox label="Offline" total={p.offline.total} per={p.offline.per} tone="offline" />
    </div>

    <button data-testid={`package-buy-${p.key}`} onClick={() => onBuy(p)}
      className="group/buy mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#A63D63] text-white px-5 py-3 text-sm hover:bg-[#8f3354] transition-all">
      Buy now · from {p.online.total} <ArrowUpRight size={15} className="group-hover/buy:translate-x-0.5 group-hover/buy:-translate-y-0.5 transition-transform" />
    </button>
  </motion.div>
);

const CONSULT_ICON = { individual: User, couples: Users };
const PKG_META = {
  foundation: { icon: Leaf, accent: { ink: "#3A7D32", soft: "#DFF0C4" } },
  growth: { icon: TrendingUp, accent: { ink: "#2E6FA6", soft: "#D8E5ED" } },
  couples_connection: { icon: Heart, accent: { ink: "#B05377", soft: "#FBEEF2" } },
  couples_growth: { icon: Sprout, accent: { ink: "#9B4A86", soft: "#F1E3F0" } },
};

export const Packages = ({ onBook, onBuy }) => {
  const individual = PACKAGES.filter((p) => p.category === "individual");
  const couples = PACKAGES.filter((p) => p.category === "couples");

  return (
    <section id="fees" className="py-24 sm:py-32 bg-[#FBEEF2]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Reveal><span className="overline">04 — Pricing & packages</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-[#20301A]">
              Choose your <em className="italic text-[#3A7D32]">therapy journey</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-sm text-[#55703F]">Different formats · different depth · one collaborative process. Sessions in Telugu, Hindi & English.</p>
          </Reveal>
        </div>

        {/* Consultation cards */}
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          className="grid md:grid-cols-2 gap-5 mb-6">
          {CONSULTATIONS.map((c) => {
            const Icon = CONSULT_ICON[c.key];
            const blush = c.tone === "blush";
            return (
              <motion.div key={c.key} variants={fadeUp} data-testid={`consult-card-${c.key}`}
                className={`rounded-3xl border p-6 sm:p-7 ${blush ? "bg-white border-[#EBD4DC]" : "bg-[#DFF0C4] border-[#C9DFAC]"}`}>
                <div className="flex items-center gap-3">
                  <span className={`grid place-items-center w-11 h-11 rounded-full ${blush ? "bg-white text-[#B05377]" : "bg-white text-[#3A7D32]"}`}><Icon size={20} /></span>
                  <h3 className={`display-serif text-lg tracking-wide ${blush ? "text-[#B05377]" : "text-[#3A7D32]"}`}>{c.title}</h3>
                </div>
                <p className="mt-3 text-sm text-[#3C5A2E] leading-relaxed">{c.blurb}</p>
                <div className="mt-4 flex gap-3">
                  <div className="flex-1 rounded-2xl bg-white border border-[#C9DFAC] px-4 py-3 text-center">
                    <div className="text-[0.6rem] font-mono uppercase tracking-widest text-[#55703F]">Online</div>
                    <div className="font-serif text-2xl text-[#20301A]">{c.online}</div>
                    <div className="text-[0.68rem] text-[#55703F]">(1 hour)</div>
                  </div>
                  <div className="flex-1 rounded-2xl bg-[#F7EBC6]/70 border border-[#EADB9B] px-4 py-3 text-center">
                    <div className="text-[0.6rem] font-mono uppercase tracking-widest text-[#55703F]">Offline</div>
                    <div className="font-serif text-2xl text-[#20301A]">{c.offline}</div>
                    <div className="text-[0.68rem] text-[#55703F]">(1 hour)</div>
                  </div>
                </div>
                <button data-testid={`consult-book-${c.key}`} onClick={onBook}
                  className={`group mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm transition-all ${blush ? "bg-[#B05377] text-white hover:bg-[#984465]" : "bg-[#3A7D32] text-[#F2F8E8] hover:bg-[#20301A]"}`}>
                  Book {c.key === "couples" ? "couples" : "a"} consultation <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Package columns */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Individual */}
          <div>
            <div className="rounded-2xl bg-[#3A7D32] text-[#F2F8E8] px-6 py-4 mb-4 flex items-center gap-3">
              <User size={20} />
              <div>
                <div className="display-serif text-lg tracking-wide">Individual Therapy Packages</div>
                <div className="text-xs text-[#C9DFAC]">Continue your journey with a package that suits you.</div>
              </div>
            </div>
            <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="grid gap-5">
              {individual.map((p) => <PackageCard key={p.key} p={p} icon={PKG_META[p.key].icon} accent={PKG_META[p.key].accent} onBuy={onBuy} />)}
            </motion.div>
          </div>
          {/* Couples */}
          <div>
            <div className="rounded-2xl bg-[#9B3F6B] text-white px-6 py-4 mb-4 flex items-center gap-3">
              <Users size={20} />
              <div>
                <div className="display-serif text-lg tracking-wide">Couples Counselling Packages</div>
                <div className="text-xs text-[#F0D7E2]">Work together towards a stronger, healthier relationship.</div>
              </div>
            </div>
            <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="grid gap-5">
              {couples.map((p) => <PackageCard key={p.key} p={p} icon={PKG_META[p.key].icon} accent={PKG_META[p.key].accent} onBuy={onBuy} />)}
            </motion.div>
          </div>
        </div>

        {/* Flash Focus band */}
        <Reveal delay={0.1}>
          <div data-testid="flash-focus" className="mt-6 rounded-3xl bg-gradient-to-r from-[#E8803C] to-[#D9632C] text-white p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Target size={22} />
                  <h3 className="display-serif text-xl tracking-wide">Flash Focus — {FLASH.sessions}</h3>
                </div>
                <p className="mt-1 text-sm text-[#FFE7D5]">A short, focused, action-oriented option.</p>
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#FFEFE3]">
                  {FLASH.points.map((li) => (
                    <li key={li} className="flex items-start gap-2"><Check size={15} className="shrink-0 mt-0.5 text-white" /> {li}</li>
                  ))}
                </ul>
              </div>
              <div className="lg:w-56 shrink-0">
                <div className="rounded-2xl bg-white/15 border border-white/30 px-5 py-4 text-center backdrop-blur-sm">
                  <div className="text-[0.6rem] font-mono uppercase tracking-widest text-[#FFE7D5]">Total</div>
                  <div className="font-serif text-4xl">{FLASH.total}</div>
                  <div className="text-xs text-[#FFE7D5]">({FLASH.per} per session)</div>
                </div>
                <button data-testid="package-buy-flash" onClick={() => onBuy(FLASH)}
                  className="group/flash mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#D9632C] px-5 py-2.5 text-sm hover:bg-[#FFEFE3] transition-all">
                  Buy Flash Focus <ArrowUpRight size={15} className="group-hover/flash:translate-x-0.5 group-hover/flash:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 text-center max-w-3xl mx-auto">
            <div className="display-serif text-base tracking-wide text-[#20301A]">Therapy is a collaborative process</div>
            <p className="mt-2 text-sm text-[#55703F]">
              Packages become eligible to use <span className="font-semibold text-[#20301A]">only after your first consultation</span>. Meaningful progress also depends on your willingness to participate, reflect and apply the work in everyday life.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
